import React, { FC, useState } from 'react';
import { Input, InputNumber, Spin, Typography } from 'antd';
import { get, merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import produce from 'immer';

import { CountryInput, DateInput, DocumentInput, Form, PhoneNumberFormItem } from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import Section from '~/layouts/Section ';
import { useValidator } from '~/controllers/validation';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { useGetApplication } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { isReadOnly } from '~/utils/isReadOnly';
import LtrInput from '~/components/inputs/LtrInput';
import { getStagePayload } from '~/utils/getStagePayload';
import CustomFields from '~/components/CustomFields';
import features from '~/constants/features';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import RefreshSharesButton from '~/components/RefreshSharesButton';

interface Props {
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any;
  n: NameFn;
  edit?: boolean;
}

const BusinessForm: FC<Props> = (props) => {
  const { onFinish, initialValues, n, edit } = props;
  const { t } = useTranslation('fillApplication');
  const [loading, setLoading] = useState<boolean>(false);

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(getFormState);

  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<'shareholders'>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields ?? {},
        errorFields: edit && stagePayload?.activeStep.errorInformation?.errorFields as any,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Container className="BusinessForm">
        <Form
          form={form}
          name={formName}
          onFinish={onFinish}
          onValuesChange={(_, newValues) => {
            optionalFieldsController.debouncedUpdate(newValues);
          }}
          initialValues={initialValues}
        >
          <Form.Item
            name={n(['data', 'uid'])}
            hidden
            initialValue={get(initialValues, n(['data', 'uid'])) || createUid()}
          />

          <section>
            <Typography.Title level={4}>{t('Company license').toUpperCase()}</Typography.Title>
            <Typography.Paragraph>{t('Please upload the company license photo.')}</Typography.Paragraph>
            <Form.Item name={n(['data', 'companyLicense', 'files'])}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item>
          </section>

          <Section>
            <Typography.Paragraph>{t('Please share basic details about the company.')}</Typography.Paragraph>
            <InputsGroupLayout>
              <Form.Item label={t('Company legal name')} name={n(['data', 'companyLicense', 'legalName'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Company legal type')} name={n(['data', 'companyLicense', 'legalType'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Company License Number')} name={n(['data', 'companyLicense', 'licenseNumber'])}>
                <LtrInput />
              </Form.Item>
              <Form.Item label={t('Date of Establishment')} name={n(['data', 'companyLicense', 'dateOfEstablishment'])}>
                <DateInput disableDateRule="date <= today" />
              </Form.Item>
            </InputsGroupLayout>
          </Section>
          <Section>
            <Typography.Title level={4}>{t('Registered company address').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item
                label={t('Country of Establishment')}
                name={n(['data', 'country'])}
                initialValue={features.defaultCountrySalesforce}
              >
                <CountryInput />
              </Form.Item>
              <Form.Item label={t('City')} name={n(['data', 'city'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('P.O Box')} name={n(['data', 'poBox'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Address')} name={n(['data', 'addressLine'])}>
                <Input />
              </Form.Item>
              <PhoneNumberFormItem
                initialValue={get(initialValues, n(['data', 'phoneNo']))}
                name={n(['data', 'phoneNo'])}
                optional
              />
            </InputsGroupLayout>
          </Section>

          <section>
            <CustomFields nFn={(name) => n(['data', ...[name].flat()])} />
          </section>

          <section>
            <Typography.Title level={4}>{t('Shares').toUpperCase()}</Typography.Title>
            <Typography.Paragraph>{t('Please specify ownership share')}</Typography.Paragraph>
            <InputsGroupLayout>
              {loading ? (
                <Spin />
              ) : (
                <Form.Item label={t('Shares %')} name={n('shares')}>
                  <InputNumber className="BusinessForm-SharesInput" max={100} />
                </Form.Item>
              )}
              <RefreshSharesButton form={form} setLoading={setLoading} shareholderType="BUSINESSES" n={n} />
            </InputsGroupLayout>
          </section>
        </Form>
      </Container>
    </FormItemContextProvider>
  );
};

export const formName = 'addNewLegalEntityShareholder';

const Container = styled.div`
  .BusinessForm-SharesInput {
    width: 100%;
  }
`;

export default BusinessForm;
